import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { isStaleChunkError, reloadOnceForStaleChunk } from '@/lib/staleChunkReload';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  componentName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // A chunk from before the latest deploy: only loading the new build helps.
    if (isStaleChunkError(error) && reloadOnceForStaleChunk()) return;
    console.error(`Error in ${this.props.componentName || 'component'}:`, error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="bg-coral-50 border-2 border-coral-300 p-4 m-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-coral-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-bold text-coral-900 mb-1">
                {this.props.componentName || 'Component'} Error
              </h3>
              <p className="text-sm text-coral-700 mb-3">
                Something went wrong while rendering this component.
              </p>
              {this.state.error && (
                <details className="mb-3">
                  <summary className="text-xs text-coral-600 cursor-pointer hover:text-coral-800 mb-1">
                    Technical Details
                  </summary>
                  <pre className="text-xs bg-coral-100 p-2 rounded overflow-auto max-h-40 text-coral-900">
                    {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </details>
              )}
              <button
                onClick={this.handleReset}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-coral-600 hover:bg-coral-700 text-white text-sm font-medium rounded transition-colors"
              >
                <RotateCcw className="h-4 w-4" />
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

